import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: 'pending' | 'active' | 'disabled';
  role: 'user' | 'admin' | 'system_admin';
  companyId: string | null;
  lineOfBusiness?: 'personal' | 'commercial' | 'both';
  consumesLicense?: boolean;
  twoFactorEnabled?: boolean;
  mustChangePassword?: boolean;
  firstLoginAt?: string | null;
  lastPasswordChangeAt?: string | null;
}

interface LoginResponse {
  requiresTwoFactor?: boolean;
  requiresMfaReenrollment?: boolean;
  requiresPasswordChange?: boolean;
  isFirstLogin?: boolean;
  tempToken?: string;
  userId?: string;
  daysSinceLastChange?: number;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  user?: User;
  securityAlerts?: Array<{
    type: string;
    severity: string;
    message: string;
  }>;
  backupCodeWarning?: {
    used: boolean;
    remaining: number;
  };
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  login: (email: string, password: string, twoFactorCode?: string) => Promise<LoginResponse>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
  securityAlerts: Array<{
    type: string;
    severity: string;
    message: string;
  }>;
  clearSecurityAlerts: () => void;
  updateUser2FAStatus: (enabled: boolean) => void;
  resetActivityTimer: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshTokenState, setRefreshTokenState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [securityAlerts, setSecurityAlerts] = useState<Array<{
    type: string;
    severity: string;
    message: string;
  }>>([]);
  
  // Inactivity timer state - use ref to avoid closure issues
  const lastActivityTimeRef = React.useRef<number>(Date.now());
  const inactivityTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Define refreshAccessToken function
  const refreshAccessToken = async (): Promise<boolean> => {
    // Read localStorage first so we always pick up rotations from another tab
    // — refresh tokens are single-use, and React state can lag localStorage
    // by a render. Falling back to React state guards against the (rare)
    // case where localStorage was cleared but the in-memory session is alive.
    const currentRefreshToken = localStorage.getItem('riskninja-refresh-token') || refreshTokenState;

    if (!currentRefreshToken) {
      // Only logout if we have no refresh token and no active token
      if (!token) {
        logout();
      }
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: currentRefreshToken }),
      });

      if (!response.ok) {
        // Only logout if explicitly unauthorized (401) - not for network errors
        if (response.status === 401) {
          console.log('🚪 Refresh token expired, logging out');
          logout();
        } else {
          console.warn('🔄 Token refresh failed, but keeping session active:', response.status);
        }
        return false;
      }

      const data = await response.json();
      
      setToken(data.accessToken);
      setRefreshTokenState(data.refreshToken);
      
      // Update localStorage
      localStorage.setItem('riskninja-token', data.accessToken);
      localStorage.setItem('riskninja-refresh-token', data.refreshToken);
      
      console.log('✅ Token refreshed successfully');
      return true;
    } catch (error) {
      console.error('Token refresh failed (network error):', error);
      // Don't logout on network errors - keep the session active
      return false;
    }
  };

  // Track last refresh time
  const lastRefreshTimeRef = React.useRef<number>(Date.now());
  const lastRefreshAttemptRef = React.useRef<number>(0);

  // Reset activity timer function with rate limiting
  const resetActivityTimer = React.useCallback(() => {
    const now = Date.now();
    lastActivityTimeRef.current = now;
    
    // Check if we should refresh token on activity
    // Refresh if it's been more than 15 minutes since last refresh (production) or 40 minutes (development)
    const isProdAPI = API_BASE_URL.includes('riskninja.ai') || API_BASE_URL.includes('api.riskninja.ai');
    const refreshThreshold = isProdAPI ? 15 * 60 * 1000 : 40 * 60 * 1000;
    const timeSinceLastRefresh = now - lastRefreshTimeRef.current;
    
    // Add minimum interval between refresh attempts to prevent rate limiting (5 minutes)
    const minRefreshInterval = 5 * 60 * 1000; // 5 minutes
    const timeSinceLastAttempt = now - lastRefreshAttemptRef.current;
    
    if (timeSinceLastRefresh > refreshThreshold && token && refreshTokenState && timeSinceLastAttempt > minRefreshInterval) {
      console.log(`🔄 Activity detected after ${Math.round(timeSinceLastRefresh / 60000)} minutes - refreshing token`);
      lastRefreshAttemptRef.current = now; // Mark attempt to prevent multiple simultaneous calls
      
      refreshAccessToken().then(success => {
        if (success) {
          lastRefreshTimeRef.current = now;
        }
      });
    } else if (timeSinceLastAttempt <= minRefreshInterval) {
      console.log('🔄 Activity detected, but refresh attempted recently - skipping to prevent rate limiting');
    } else {
      console.log('🔄 Activity detected, timer reset');
    }
  }, [token, refreshTokenState]);

  // Activity detection effect
  useEffect(() => {
    const handleActivity = () => {
      resetActivityTimer();
    };

    // Add event listeners for user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [resetActivityTimer]);

  // Inactivity logout timer
  useEffect(() => {
    if (token) {
      // Clear existing timeout
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }

      // Check for inactivity every minute
      const checkInactivity = () => {
        const now = Date.now();
        const timeSinceLastActivity = now - lastActivityTimeRef.current;
        const inactivityLimit = 60 * 60 * 1000; // 1 hour

        console.log(`⏰ Inactivity check: ${Math.round(timeSinceLastActivity / 60000)} minutes since last activity`);

        if (timeSinceLastActivity >= inactivityLimit) {
          console.log('🚪 User inactive for 1 hour, logging out...');
          logout();
        } else {
          // Schedule next check
          inactivityTimeoutRef.current = setTimeout(checkInactivity, 60 * 1000); // Check every minute
        }
      };

      // Start checking
      inactivityTimeoutRef.current = setTimeout(checkInactivity, 60 * 1000);
    }

    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
    };
  }, [token]); // Removed lastActivityTime dependency to prevent timer restart on every activity

  // Handle 401 responses dispatched by fetchInterceptor / apiClient.
  // Just attempt a refresh — refreshAccessToken already logs out for the
  // unrecoverable cases (no refresh token, or refresh endpoint returns 401).
  // For transient failures (network blip, 500/502, multi-tab token race) we
  // keep the session and let the next refresh tick try again; aggressively
  // logging out here was kicking active users off mid-session.
  const unauthorizedHandlingRef = React.useRef<boolean>(false);
  useEffect(() => {
    const handleUnauthorized = async () => {
      if (unauthorizedHandlingRef.current) return;
      if (!token) return; // Already logged out — nothing to do
      unauthorizedHandlingRef.current = true;

      try {
        await refreshAccessToken();
      } finally {
        unauthorizedHandlingRef.current = false;
      }
    };

    window.addEventListener('api:unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('api:unauthorized', handleUnauthorized);
    };
  }, [token, refreshTokenState]);

  // Auto-refresh token timer
  useEffect(() => {
    let refreshTimer: NodeJS.Timeout;

    if (token && refreshTokenState) {
      // Dynamically determine refresh interval based on environment
      // Production: 30min tokens → refresh every 20min (10min buffer)
      // Development: 60min tokens → refresh every 50min (10min buffer)
      const isProdAPI = API_BASE_URL.includes('riskninja.ai') || API_BASE_URL.includes('api.riskninja.ai');
      const refreshInterval = isProdAPI 
        ? 20 * 60 * 1000  // 20 minutes for production
        : 50 * 60 * 1000; // 50 minutes for development
      
      console.log(`🔄 Setting token refresh interval: ${refreshInterval / 60000} minutes (${isProdAPI ? 'production' : 'development'} environment)`);
      
      refreshTimer = setInterval(() => {
        refreshAccessToken().then(success => {
          if (success) {
            lastRefreshTimeRef.current = Date.now();
          }
        });
      }, refreshInterval);
    }

    return () => {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
    };
  }, [token, refreshTokenState]);

  // Load tokens from localStorage on mount
  useEffect(() => {
    // Decode a JWT's exp claim and report whether it's already expired.
    // Returns false (treat as valid) for anything we can't parse so we never
    // force a logout based on a token format we don't understand — a genuine
    // 401 from the API still flows through the normal refresh/logout path.
    const isJwtExpired = (jwt: string): boolean => {
      try {
        const parts = jwt.split('.');
        if (parts.length !== 3) return false;
        const payload = JSON.parse(
          atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
        );
        if (!payload.exp) return false;
        // 5s skew buffer so a token about to expire is treated as expired.
        return Date.now() >= payload.exp * 1000 - 5000;
      } catch {
        return false;
      }
    };

    // Hard-clear a dead session so ProtectedRoute redirects to /login instead
    // of rendering a "logged in" shell that can't load any data.
    const clearStoredSession = () => {
      setUser(null);
      setToken(null);
      setRefreshTokenState(null);
      localStorage.removeItem('riskninja-token');
      localStorage.removeItem('riskninja-refresh-token');
      localStorage.removeItem('riskninja-user');
      localStorage.removeItem('riskninja-background-reports');
    };

    // Attempt a silent refresh at startup when the access token is expired but
    // a refresh token is present. Returns true only when we get a fresh,
    // usable token pair back.
    const restoreViaRefresh = async (
      refreshTokenValue: string,
      parsedUser: User
    ): Promise<boolean> => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: refreshTokenValue }),
        });
        if (!response.ok) return false;
        const data = await response.json();
        if (!data.accessToken || !data.refreshToken) return false;

        setToken(data.accessToken);
        setRefreshTokenState(data.refreshToken);
        setUser(parsedUser);
        localStorage.setItem('riskninja-token', data.accessToken);
        localStorage.setItem('riskninja-refresh-token', data.refreshToken);
        console.log('✅ Session restored via token refresh');
        return true;
      } catch (e) {
        console.warn('Startup token refresh failed:', e);
        return false;
      }
    };

    const initSession = async () => {
      // Check for impersonation token in URL first
      const urlParams = new URLSearchParams(window.location.search);
      const impersonationToken = urlParams.get('impersonation_token');
      const impersonatedUserStr = urlParams.get('impersonated_user');

      if (impersonationToken && impersonatedUserStr) {
        try {
          const impersonatedUser = JSON.parse(decodeURIComponent(impersonatedUserStr));

          // Clear user-specific data to prevent leakage (but keep theme/settings)
          const keysToRemove = [
            'riskninja-token',
            'riskninja-refresh-token',
            'riskninja-user',
            'selected-customer',
            'selected-project',
            'currentCustomer',
            'currentProject',
            'customers',
            'projects',
            'documents',
            'reports'
          ];
          keysToRemove.forEach(key => localStorage.removeItem(key));

          // Set authentication with impersonation token FIRST
          setToken(impersonationToken);
          setUser(impersonatedUser);

          // Then store in localStorage
          localStorage.setItem('riskninja-token', impersonationToken);
          localStorage.setItem('riskninja-user', JSON.stringify(impersonatedUser));

          // Clean up URL by removing the token parameters
          const cleanUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, document.title, cleanUrl);

          console.log('🔐 Impersonation session activated:', impersonatedUser.email);
          setIsLoading(false);
          return;
        } catch (error) {
          console.error('Failed to parse impersonation data:', error);
        }
      }

      // Otherwise, check localStorage for existing session
      const savedToken = localStorage.getItem('riskninja-token');
      const savedRefreshToken = localStorage.getItem('riskninja-refresh-token');
      const savedUser = localStorage.getItem('riskninja-user');

      if (savedToken && savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser) as User;

          if (isJwtExpired(savedToken)) {
            // Access token has already expired (user has been away). Try to
            // silently refresh before showing the app; if there's no refresh
            // token or the refresh fails, log out cleanly so the user lands on
            // the login screen instead of an empty, broken session.
            if (savedRefreshToken) {
              const refreshed = await restoreViaRefresh(savedRefreshToken, parsedUser);
              if (!refreshed) {
                console.log('🚪 Stored token expired and refresh failed — logging out');
                clearStoredSession();
              }
            } else {
              console.log('🚪 Stored token expired and no refresh token — logging out');
              clearStoredSession();
            }
          } else {
            setToken(savedToken);
            setRefreshTokenState(savedRefreshToken);
            setUser(parsedUser);
            console.log('🔄 Session restored from localStorage');
          }
        } catch (error) {
          console.error('Failed to parse saved user data:', error);
          clearStoredSession();
        }
      }
      setIsLoading(false);
    };

    initSession();
  }, []);

  const login = async (email: string, password: string, twoFactorCode?: string): Promise<LoginResponse> => {
    setIsLoading(true);
    setError(null);
    setSecurityAlerts([]);
    
    const loginUrl = `${API_BASE_URL}/api/auth/login`;
    console.log('[AuthContext] login URL:', loginUrl);
    
    try {
      const requestBody: any = { email, password };
      if (twoFactorCode) {
        requestBody.twoFactorCode = twoFactorCode;
      }

      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 423) {
          // Account locked
          throw new Error(data.error || 'Account temporarily locked');
        }
        // Server auto-reset an unreadable 2FA record (e.g. ENCRYPTION_KEY
        // changed since enrollment). Surface this as a structured response
        // so the login page can show a clear re-enroll notice instead of
        // looking like a wrong-code error.
        if (data?.code === 'MFA_REENROLLMENT_REQUIRED') {
          return { requiresMfaReenrollment: true };
        }
        throw new Error(data.error || 'Login failed');
      }

      // Handle 2FA requirement
      console.log('[AuthContext] Login response data:', data);
      if (data.requiresTwoFactor) {
        console.log('[AuthContext] Returning requiresTwoFactor: true');
        return { requiresTwoFactor: true };
      }

      // Handle password change requirement
      if (data.requiresPasswordChange) {
        console.log('[AuthContext] Returning requiresPasswordChange: true');
        return { 
          requiresPasswordChange: true,
          isFirstLogin: data.isFirstLogin,
          tempToken: data.tempToken,
          userId: data.userId,
          daysSinceLastChange: data.daysSinceLastChange
        };
      }

      // Successful login with full authentication
      if (data.accessToken && data.refreshToken && data.user) {
        setToken(data.accessToken);
        setRefreshTokenState(data.refreshToken);
        setUser(data.user);
        
        // Save to localStorage
        localStorage.setItem('riskninja-token', data.accessToken);
        localStorage.setItem('riskninja-refresh-token', data.refreshToken);
        localStorage.setItem('riskninja-user', JSON.stringify(data.user));

        // Handle security alerts
        if (data.securityAlerts && data.securityAlerts.length > 0) {
          setSecurityAlerts(data.securityAlerts);
        }

        return {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          user: data.user,
          securityAlerts: data.securityAlerts,
          backupCodeWarning: data.backupCodeWarning
        };
      }

      // For valid responses that don't include tokens (2FA, password change), return the data as-is
      return data;
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    const registerUrl = `${API_BASE_URL}/api/auth/register`;
    console.log('[AuthContext] register URL:', registerUrl);
    
    try {
      const response = await fetch(registerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, firstName, lastName }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      setToken(data.token);
      setUser(data.user);
      
      // Save to localStorage
      localStorage.setItem('riskninja-token', data.token);
      localStorage.setItem('riskninja-user', JSON.stringify(data.user));
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Registration failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Call logout endpoint to blacklist current token
      if (token) {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with local logout even if API call fails
    }

    // Clear local state
    setUser(null);
    setToken(null);
    setRefreshTokenState(null);
    setError(null);
    setSecurityAlerts([]);
    
    // Clear localStorage
    localStorage.removeItem('riskninja-token');
    localStorage.removeItem('riskninja-refresh-token');
    localStorage.removeItem('riskninja-user');
    localStorage.removeItem('riskninja-background-reports');
  };

  const clearSecurityAlerts = (): void => {
    setSecurityAlerts([]);
  };

  const updateUser2FAStatus = (enabled: boolean): void => {
    if (user) {
      const updatedUser = { ...user, twoFactorEnabled: enabled };
      setUser(updatedUser);
      localStorage.setItem('riskninja-user', JSON.stringify(updatedUser));
    }
  };

  const value: AuthContextType = {
    user,
    token,
    refreshToken: refreshTokenState,
    login,
    register,
    logout,
    refreshAccessToken,
    isLoading,
    error,
    securityAlerts,
    clearSecurityAlerts,
    updateUser2FAStatus,
    resetActivityTimer,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 